import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import { Package, TrendingUp, AlertTriangle, DollarSign, RefreshCw, ArrowUpRight, ArrowDownRight, Minus, FileBarChart2, Play } from 'lucide-react';
import { toast } from 'sonner';

import { useNavigate } from 'react-router-dom';

const COLORS = ['#F97316', '#3B82F6', '#10B981', '#8B5CF6', '#EF4444', '#F59E0B'];

const fmt = (n) => new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(n);
const fmtCur = (n) => `₹${fmt(n)}`;

function KPICard({ title, value, subtitle, icon: Icon, color, testid, to }) {
  const navigate = useNavigate();
  return (
    <div data-testid={testid} onClick={() => to && navigate(to)}
      className={`bg-white border border-slate-200 rounded-sm p-5 flex items-start gap-4 shadow-sm hover:shadow-md transition-all ${to ? 'cursor-pointer hover:border-slate-300' : ''}`}>
      <div className={`w-10 h-10 rounded-sm flex items-center justify-center flex-shrink-0 ${color}`}>
        <Icon size={18} className="text-white" />
      </div>
      <div className="min-w-0">
        <p className="text-xs font-bold uppercase text-slate-500 tracking-wider mb-1">{title}</p>
        <p className="font-mono text-2xl font-bold text-slate-900 leading-none">{value}</p>
        {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
      </div>
    </div>
  );
}

function ChangeIndicator({ pct }) {
  if (pct > 0) return <span className="flex items-center gap-0.5 text-red-500 text-xs font-mono"><ArrowUpRight size={12} />+{pct.toFixed(1)}%</span>;
  if (pct < 0) return <span className="flex items-center gap-0.5 text-green-500 text-xs font-mono"><ArrowDownRight size={12} />{pct.toFixed(1)}%</span>;
  return <span className="flex items-center gap-0.5 text-slate-400 text-xs font-mono"><Minus size={12} />0%</span>;
}

function MarginBadge({ pct }) {
  if (pct >= 20) return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">{pct.toFixed(1)}%</span>;
  if (pct >= 15) return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">{pct.toFixed(1)}%</span>;
  return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200">{pct.toFixed(1)}%</span>;
}

function LastReviewCard({ review }) {
  const navigate = useNavigate();
  const [running, setRunning] = useState(false);
  const handleRun = async (e) => {
    e.stopPropagation();
    setRunning(true);
    try {
      await api.post('/reports/market-review/run');
      toast.success('Market review complete! Check Reports for details.');
      navigate('/reports');
    } catch { toast.error('Review failed'); }
    finally { setRunning(false); }
  };
  if (!review) return (
    <div className="bg-slate-50 border border-dashed border-slate-300 rounded-sm p-4 flex items-center justify-between" data-testid="last-review-card">
      <div className="flex items-center gap-3">
        <FileBarChart2 size={18} className="text-slate-400" />
        <div>
          <p className="text-sm font-semibold text-slate-600">No Market Review yet</p>
          <p className="text-xs text-slate-400">Run a review to compare locked vs current market RM prices</p>
        </div>
      </div>
      <button onClick={handleRun} disabled={running}
        data-testid="dashboard-run-review-btn"
        className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-900 text-white text-xs font-bold uppercase rounded-sm hover:bg-slate-800 transition-colors">
        {running ? <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Play size={11} />}
        Run Now
      </button>
    </div>
  );
  const s = review.summary || {};
  const hasIssues = (s.rm_behind_count || 0) + (s.margin_at_risk_count || 0) > 0;
  return (
    <div onClick={() => navigate('/reports')} data-testid="last-review-card"
      className={`cursor-pointer rounded-sm p-4 flex items-center justify-between gap-4 border transition-all hover:shadow-md ${
        hasIssues ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200'
      }`}>
      <div className="flex items-center gap-3 min-w-0">
        <FileBarChart2 size={18} className={hasIssues ? 'text-red-500' : 'text-emerald-500'} />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-800">
            Last Market Review · <span className="font-mono">{review.created_at ? new Date(review.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</span>
          </p>
          <p className="text-xs text-slate-500">{review.total_parts} parts reviewed</p>
        </div>
      </div>
      <div className="flex items-center gap-4 flex-shrink-0">
        {s.rm_behind_count > 0 && <span className="text-xs font-semibold text-red-700 bg-red-100 border border-red-200 rounded px-2 py-1">{s.rm_behind_count} RM behind</span>}
        {s.margin_at_risk_count > 0 && <span className="text-xs font-semibold text-amber-700 bg-amber-100 border border-amber-200 rounded px-2 py-1">{s.margin_at_risk_count} margin at risk</span>}
        {!hasIssues && <span className="text-xs font-semibold text-emerald-700 bg-emerald-100 border border-emerald-200 rounded px-2 py-1">All clear</span>}
        <button
          data-testid="dashboard-run-review-again-btn"
          disabled={running}
          onClick={(e) => { e.stopPropagation(); handleRun(e); }}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-900 text-white text-xs font-bold uppercase rounded-sm hover:bg-slate-800 transition-colors">
          {running ? <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Play size={11} />}
          Re-run
        </button>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const res = await api.get('/dashboard/stats');
      setStats(res.data);
    } catch {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStats(); }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <div key={i} className="h-24 bg-slate-200 rounded-sm animate-pulse" />)}
        </div>
        <div className="h-80 bg-slate-200 rounded-sm animate-pulse" />
      </div>
    );
  }

  if (!stats) return null;

  const trendMaterialKeys = stats.trend_material_keys || [];

  return (
    <div className="space-y-6 max-w-[1920px]" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading font-bold text-slate-900 text-xl">Platform Overview</h2>
          <p className="text-slate-500 text-sm mt-0.5">Real-time costing intelligence for your parts portfolio</p>
        </div>
        <button
          onClick={fetchStats}
          data-testid="refresh-dashboard"
          className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-300 rounded-sm text-slate-700 text-sm font-semibold hover:bg-slate-50 transition-colors shadow-sm"
        >
          <RefreshCw size={14} />
          Refresh
        </button>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kpi-cards">
        <KPICard testid="kpi-total-parts" title="Total FG Parts" value={stats.total_parts} subtitle="Active in database" icon={Package} color="bg-slate-700" to="/parts" />
        <KPICard testid="kpi-rm-materials" title="RM Grades" value={stats.total_materials} subtitle={`Across ${stats.material_categories || 5} categories`} icon={TrendingUp} color="bg-blue-600" to="/materials" />
        <KPICard testid="kpi-margin-risk" title="Margin at Risk" value={stats.margin_at_risk} subtitle="Parts below 15% margin" icon={AlertTriangle} color={stats.margin_at_risk > 0 ? "bg-red-500" : "bg-emerald-500"} to="/parts?margin_risk=true" />
        <KPICard testid="kpi-contribution" title="Total Contribution" value={fmtCur(stats.total_contribution)} subtitle="Across all parts" icon={DollarSign} color="bg-orange-500" to="/parts" />
      </div>

      {/* Stale RM prices bubble — grades not price-updated in 30+ days */}
      {stats.rm_stale_count > 0 && (
        <Link to="/materials" data-testid="rm-stale-banner"
          className="flex items-center justify-between gap-3 rounded-sm p-4 border bg-amber-50 border-amber-200 hover:shadow-md transition-all">
          <div className="flex items-center gap-3 min-w-0">
            <span className="flex items-center justify-center w-9 h-9 rounded-full bg-amber-500 text-white font-black text-sm flex-shrink-0">{stats.rm_stale_count}</span>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-amber-900">
                {stats.rm_stale_count} RM grade{stats.rm_stale_count !== 1 ? 's' : ''} not updated in {stats.rm_stale_days || 30}+ days
              </p>
              <p className="text-xs text-amber-700">Refresh their market prices to keep variance accurate — click to open RM Price Index</p>
            </div>
          </div>
          <ArrowUpRight size={16} className="text-amber-600 flex-shrink-0" />
        </Link>
      )}

      {/* Last Market Review Card */}
      <LastReviewCard review={stats.last_review} />

      {/* Charts Row */}
      <div className="grid grid-cols-12 gap-4">
        {/* RM Price Trend Chart */}
        <div className="col-span-12 lg:col-span-8 bg-white border border-slate-200 rounded-sm shadow-sm" data-testid="rm-trend-chart">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <div>
              <h3 className="font-heading font-bold text-slate-900 text-sm">Raw Material Price Trend</h3>
              <p className="text-slate-500 text-xs mt-0.5">Last 90 days — Top materials by price</p>
            </div>
            <Link to="/materials" className="text-orange-500 text-xs font-semibold hover:text-orange-600 flex items-center gap-1">
              Update Prices <ArrowUpRight size={12} />
            </Link>
          </div>
          <div className="p-5" style={{ height: '320px' }}>
            {stats.rm_trend_data && stats.rm_trend_data.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={stats.rm_trend_data} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fontFamily: 'JetBrains Mono' }} />
                  <YAxis tick={{ fontSize: 11, fontFamily: 'JetBrains Mono' }} tickFormatter={(v) => `₹${v}`} />
                  <Tooltip
                    formatter={(value, name) => [`₹${value}/kg`, name]}
                    contentStyle={{ fontSize: '12px', fontFamily: 'Manrope', borderRadius: '2px', border: '1px solid #e2e8f0' }}
                  />
                  <Legend wrapperStyle={{ fontSize: '11px', fontFamily: 'Manrope' }} />
                  {trendMaterialKeys.map((key, i) => (
                    <Line key={key} type="monotone" dataKey={key} stroke={COLORS[i % COLORS.length]}
                      strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} connectNulls />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-slate-400 text-sm">
                No price history data available
              </div>
            )}
          </div>
        </div>

        {/* Recent RM Changes */}
        <div className="col-span-12 lg:col-span-4 bg-white border border-slate-200 rounded-sm shadow-sm" data-testid="recent-rm-changes">
          <div className="px-5 py-4 border-b border-slate-100">
            <h3 className="font-heading font-bold text-slate-900 text-sm">Recent RM Changes</h3>
            <p className="text-slate-500 text-xs mt-0.5">Latest price updates</p>
          </div>
          <div className="divide-y divide-slate-100 max-h-[320px] overflow-y-auto">
            {stats.recent_rm_changes?.length > 0 ? stats.recent_rm_changes.map((change, i) => (
              <div key={i} className="px-5 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors">
                <div>
                  <p className="text-sm font-semibold text-slate-800">{change.material}</p>
                  <p className="font-mono text-xs text-slate-500">₹{change.old_price}/kg → ₹{change.new_price}/kg</p>
                </div>
                <ChangeIndicator pct={change.change_pct} />
              </div>
            )) : (
              <div className="px-5 py-8 text-center text-slate-400 text-sm">No recent changes</div>
            )}
          </div>
        </div>
      </div>

      {/* Second Charts Row */}
      <div className="grid grid-cols-12 gap-4">
        {/* Customer Distribution */}
        <div className="col-span-12 lg:col-span-6 bg-white border border-slate-200 rounded-sm shadow-sm" data-testid="customer-distribution-chart">
          <div className="px-5 py-4 border-b border-slate-100">
            <h3 className="font-heading font-bold text-slate-900 text-sm">Parts by Customer</h3>
            <p className="text-slate-500 text-xs mt-0.5">Distribution of FG parts across customers</p>
          </div>
          <div className="p-5" style={{ height: '260px' }}>
            {stats.customer_distribution?.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.customer_distribution} margin={{ top: 5, right: 20, left: 0, bottom: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 10, fontFamily: 'Manrope' }} angle={-30} textAnchor="end" interval={0} />
                  <YAxis tick={{ fontSize: 11, fontFamily: 'JetBrains Mono' }} allowDecimals={false} />
                  <Tooltip contentStyle={{ fontSize: '12px', borderRadius: '2px', border: '1px solid #e2e8f0' }} />
                  <Bar dataKey="count" name="Parts" fill="#0F172A" radius={[2, 2, 0, 0]}>
                    {stats.customer_distribution.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-slate-400 text-sm">No data</div>
            )}
          </div>
        </div>

        {/* Unread Alerts Feed */}
        <div className="col-span-12 lg:col-span-6 bg-white border border-slate-200 rounded-sm shadow-sm" data-testid="alerts-feed">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <div>
              <h3 className="font-heading font-bold text-slate-900 text-sm">Active Alerts</h3>
              <p className="text-slate-500 text-xs mt-0.5">{stats.unread_alerts} unread alerts</p>
            </div>
            <Link to="/alerts" className="text-orange-500 text-xs font-semibold hover:text-orange-600">View All</Link>
          </div>
          <div className="divide-y divide-slate-100 max-h-[260px] overflow-y-auto">
            {stats.at_risk_parts?.length > 0 ? stats.at_risk_parts.slice(0, 6).map((part, i) => (
              <div key={i} className="px-5 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors">
                <div className="min-w-0">
                  <p className="font-mono text-xs font-semibold text-slate-700">{part.part_number}</p>
                  <p className="text-xs text-slate-500 truncate">{part.part_name} · {part.customer_name}</p>
                </div>
                <MarginBadge pct={part.margin_pct} />
              </div>
            )) : (
              <div className="px-5 py-8 text-center">
                <p className="text-emerald-600 text-sm font-semibold">All parts within margin targets</p>
                <p className="text-slate-400 text-xs mt-1">No margin risk detected</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* At Risk Parts Table */}
      {stats.at_risk_parts?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-sm shadow-sm" data-testid="at-risk-table">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <div>
              <h3 className="font-heading font-bold text-slate-900 text-sm">Parts Requiring Attention</h3>
              <p className="text-slate-500 text-xs mt-0.5">Parts with margin below 15% or exceeding approved cost</p>
            </div>
            <Link to="/parts" className="text-orange-500 text-xs font-semibold hover:text-orange-600 flex items-center gap-1">
              Manage Parts <ArrowUpRight size={12} />
            </Link>
          </div>
          <div className="overflow-x-auto">
            <table className="data-table w-full">
              <thead>
                <tr>
                  <th className="text-left">Part Number</th>
                  <th className="text-left">Part Name</th>
                  <th className="text-left">Customer</th>
                  <th className="text-left">Material</th>
                  <th className="text-right">Selling Price</th>
                  <th className="text-right">Total Cost</th>
                  <th className="text-right">Margin %</th>
                </tr>
              </thead>
              <tbody>
                {stats.at_risk_parts.map((part, i) => (
                  <tr key={i}>
                    <td className="font-mono font-semibold text-slate-800">{part.part_number}</td>
                    <td className="text-slate-700">{part.part_name}</td>
                    <td className="text-slate-600">{part.customer_name}</td>
                    <td className="text-slate-600">{part.material}</td>
                    <td className="font-mono text-right">{fmtCur(part.selling_price)}</td>
                    <td className="font-mono text-right">{fmtCur(part.total_manufacturing_cost)}</td>
                    <td className="text-right"><MarginBadge pct={part.margin_pct} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
